<script lang="ts">
	import { fly } from 'svelte/transition';
	import IconArrowUp from '~icons/mdi/arrow-up';
	import IconPartyPopper from '~icons/mdi/party-popper';

	const MIN_SCROLL_Y = 1200;

	let scrollY = $state<number>(0);
	let isCelebrating = $state<boolean>(false);

	let scrollProgress = $derived.by(() => {
		const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
		if (maxScroll <= 0) return 0;
		return Math.min(100, Math.max(0, (scrollY / maxScroll) * 100));
	});
	const isAtBottom = $derived<boolean>(scrollProgress >= 96);

	function scrollToTop() {
		window.scrollTo({ top: 0, behavior: 'smooth' });
	}

	$effect(() => {
		let timer: ReturnType<typeof setTimeout> | undefined;
		const celebrationDurationMs: number = 2500;
		if (isAtBottom) {
			isCelebrating = true;
			timer = setTimeout(() => {
				isCelebrating = false;
			}, celebrationDurationMs);
		} else {
			isCelebrating = false;
		}

		return () => {
			if (timer) clearTimeout(timer);
		};
	});
</script>

<svelte:window bind:scrollY />

{#if scrollY >= MIN_SCROLL_Y}
	<button
		class={{
			'btn fixed right-6 bottom-6 z-40 btn-circle p-0 shadow-lg transition-colors duration-500': true,
			'shadow-success/50 btn-success': isCelebrating,
			'btn-secondary': !isCelebrating
		}}
		onclick={scrollToTop}
		aria-label={`Back to top — ${Math.round(scrollProgress)}% down the page`}
		transition:fly={{ y: 20, duration: 300 }}
	>
		<span class="relative flex h-full w-full items-center justify-center">
			<svg class="pointer-events-none absolute inset-0 h-full w-full -rotate-90" viewBox="0 0 100 100" aria-hidden="true">
				<circle class="opacity-20" cx="50" cy="50" r="46" fill="none" stroke="currentColor" stroke-width="4" />
				<circle
					class="transition-opacity duration-300 {isCelebrating ? 'opacity-100' : 'opacity-55'}"
					cx="50"
					cy="50"
					r="46"
					fill="none"
					stroke="currentColor"
					stroke-width="4"
					stroke-linecap="round"
					pathLength="100"
					stroke-dasharray="100"
					stroke-dashoffset={100 - scrollProgress}
				/>
			</svg>

			<div class="relative h-6 w-6">
				{#if isCelebrating}
					<div
						class="absolute inset-0 flex items-center justify-center"
						in:fly={{ y: 20, duration: 300 }}
						out:fly={{ y: -20, duration: 200 }}
					>
						<IconPartyPopper class="animate-bounce" />
					</div>
				{:else}
					<div
						class="absolute inset-0 flex items-center justify-center"
						in:fly={{ y: 20, duration: 300 }}
						out:fly={{ y: -20, duration: 200 }}
					>
						<IconArrowUp />
					</div>
				{/if}
			</div>
		</span>
	</button>
{/if}
